import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { z } from "zod";
import {
  ServiceTypeEnum,
  adminCreateLocationWithCustomerReqDtoSchema,
  useAdminLeadControllerGetOne,
} from "service";
import {
  AdminCreateLocationWithCustomerReqDto,
  AdminLeadResDto,
} from "service/api/estabrookSchemas";
import { generateRoute, makeSxStyles } from "ui";
import Navbar from "@/components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "@/shared/components/header-items/HeaderItems";
import DashboardContentLayout from "@/shared/layouts/DashboardContentLayout";
import useUnsavedChangesBlocker from "@/shared/hooks/useUnsavedChangesBlocker";
import useAddressValidation from "@/hooks/use-address-validation";
import CrmDetailsFields from "@/shared/components/crm/CrmDetailsFields";
import CustomerFinancesSection from "@/shared/components/crm/CustomerFinancesSection";
import { fieldLabelSx } from "@/shared/components/crm/form-typography";
import { useNumericFields } from "@/shared/hooks/useNumericFields";
import {
  crmFieldValidations,
  requiredNameValidations,
} from "@/shared/utils/crm-validation";
import { numericFieldSx } from "@/shared/utils/numeric-input";
import { AdRoutes } from "@/constants/ad-routes";
import useConvertLeadToCustomer, {
  ConvertedTarget,
} from "../hooks/useConvertLeadToCustomer";

// The lead DTO types most CRM fields as Record<string, any>, but the API
// returns primitives (contractPrice as a string), so coerce when seeding.
const asString = (value: unknown): string | undefined =>
  value == null ? undefined : String(value);
const asNumber = (value: unknown): number | undefined => {
  if (value == null || value === "") return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
};

// serviceTime is left blank initially (not on a lead — the admin must enter it),
// so allow undefined in the form; the schema enforces a value >= 1 before submit.
type ConvertFormValues = Omit<
  AdminCreateLocationWithCustomerReqDto,
  "serviceTime"
> & { serviceTime?: number };

// The address is prefilled from the lead (already created/geocoded), so we don't
// apply the "must match a live autocomplete prediction" refinement here — that
// would reject the prefilled value since predictions only load while typing.
// Keep the base validations + enforce Service Duration >= 1.
const convertValidationSchema =
  adminCreateLocationWithCustomerReqDtoSchema.extend({
    serviceTime: z
      .number({
        required_error: "Service duration is required",
        invalid_type_error: "Service duration is required",
      })
      .min(1, "Service duration must be at least 1 minute"),
    ...requiredNameValidations,
    address: z.string().min(1, "Address is required"),
    ...crmFieldValidations,
  });

const emptyValues: ConvertFormValues = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "+",
  address: "",
  serviceTime: undefined,
  type: ServiceTypeEnum.annual,
  reportsEnabled: false,
  notes: "",
  onsiteContactName: "",
  onsiteContactEmail: "",
  contractPrice: undefined,
  billingAddress: "",
  financialNotes: "",
  squareFootage: undefined,
  slope: undefined,
  condition: undefined,
  difficulty: undefined,
};

// serviceTime / type are not on a lead — the admin fills them before saving.
const toFormValues = (lead: AdminLeadResDto): ConvertFormValues => ({
  ...emptyValues,
  email: lead.email,
  firstName: lead.firstName,
  lastName: lead.lastName,
  phone: lead.phoneNumber,
  address: lead.address,
  notes: asString(lead.notes) ?? "",
  onsiteContactName: asString(lead.onsiteContactName) ?? "",
  onsiteContactEmail: asString(lead.onsiteContactEmail) ?? "",
  contractPrice: asNumber(lead.contractPrice),
  billingAddress: asString(lead.billingAddress) ?? "",
  financialNotes: asString(lead.financialNotes) ?? "",
  squareFootage: asNumber(lead.squareFootage),
  slope: lead.slope || undefined,
  condition: lead.condition || undefined,
  difficulty: asNumber(lead.difficulty),
});

const ConvertLeadPage: FC = () => {
  const navigate = useNavigate();
  const { leadId = "" } = useParams();
  const styles = useStyles();
  // CRM sections (Onsite Contact + Driveway Info) — expanded by default so the
  // prefilled lead data is visible.
  const [showMore, setShowMore] = useState(true);
  const [target, setTarget] = useState<ConvertedTarget | null>(null);

  const { getPlacePredictions, placePredictions } = useAddressValidation();

  const { data, isLoading } = useAdminLeadControllerGetOne({
    pathParams: { leadId },
  });
  const lead = data?.data;

  const { convertLead, isConverting } = useConvertLeadToCustomer((t) =>
    setTarget(t),
  );

  const form = useFormik<ConvertFormValues>({
    initialValues: emptyValues,
    validationSchema: toFormikValidationSchema(convertValidationSchema),
    onSubmit: (values) =>
      convertLead({
        email: values.email.toLowerCase(),
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        address: values.address,
        serviceTime: values.serviceTime ?? 0,
        type: values.type,
        reportsEnabled: values.reportsEnabled,
        notes: values.notes,
        onsiteContactName: values.onsiteContactName || undefined,
        onsiteContactEmail: values.onsiteContactEmail || undefined,
        contractPrice: values.contractPrice,
        billingAddress: values.billingAddress || undefined,
        financialNotes: values.financialNotes || undefined,
        squareFootage: values.squareFootage,
        slope: values.slope,
        condition: values.condition,
        difficulty: values.difficulty,
        leadId,
      }),
  });

  // Seed once the lead loads, but never over in-progress edits.
  useEffect(() => {
    if (lead && !form.dirty) {
      form.resetForm({ values: toFormValues(lead) });
    }
  }, [lead]);

  useUnsavedChangesBlocker(form.dirty && !target);

  useEffect(() => {
    if (target?.customerId) {
      navigate(
        generateRoute(AdRoutes.customerDetailAddress, {
          customerId: target.customerId,
          addressId: target.addressId || "",
        }),
        { replace: true },
      );
    }
  }, [target, navigate]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    validateField,
    submitForm,
  } = form;

  // Service Duration is a positive magnitude in minutes, so it gets the same
  // treatment as the CRM numbers: no typed "-"/"e"/letters, and no stepping
  // (the schema's >= 1 rule is what enforces the sign — this just keeps input
  // that can only fail it out of the field). Decimals stay allowed; the
  // generated schema types serviceTime as a plain number, not an integer.
  const numeric = useNumericFields({
    setFieldValue,
    setFieldTouched,
    handleBlur,
  });

  const serviceTime = numeric.field({
    name: "serviceTime",
    value: values.serviceTime,
    error: errors.serviceTime,
    touched: touched.serviceTime,
    allowDecimal: true,
  });

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              { label: "Leads", url: AdRoutes.leads },
              { label: "Convert to Customer" },
            ]}
          />
          <HeaderItemsRight>
            <Button
              variant="contained"
              disabled={isConverting || !form.dirty || !form.isValid}
              onClick={() => submitForm()}
              data-testid="save-customer"
            >
              Save Customer
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: "40px" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={styles.container}>
          <Typography sx={styles.sectionTitle}>Customer Info</Typography>
          <Typography sx={fieldLabelSx}>First Name *</Typography>
          <TextField
            id="firstName"
            value={values.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.firstName && !!errors.firstName}
            helperText={touched.firstName && errors.firstName}
            margin="dense"
            variant="outlined"
            fullWidth
          />
          <Typography sx={fieldLabelSx}>Last Name *</Typography>
          <TextField
            id="lastName"
            value={values.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.lastName && !!errors.lastName}
            helperText={touched.lastName && errors.lastName}
            margin="dense"
            variant="outlined"
            fullWidth
          />
          <Typography sx={fieldLabelSx}>Email Address *</Typography>
          <TextField
            id="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.email && !!errors.email}
            helperText={touched.email && errors.email}
            margin="dense"
            variant="outlined"
            fullWidth
          />
          <Typography sx={fieldLabelSx}>Phone number *</Typography>
          <TextField
            id="phone"
            type="tel"
            value={values.phone}
            onChange={(e) => {
              const { value } = e.target;
              setFieldValue("phone", value.length === 0 ? "+" : value);
              setFieldTouched("phone", true);
            }}
            onBlur={handleBlur}
            error={touched.phone && !!errors.phone}
            helperText={touched.phone && errors.phone}
            margin="dense"
            variant="outlined"
            fullWidth
          />

          <Typography sx={styles.sectionTitle}>Address Info</Typography>
          <Typography sx={fieldLabelSx}>Address *</Typography>
          <Autocomplete
            freeSolo
            filterOptions={(x) => x}
            options={placePredictions.map((prediction) => ({
              label: prediction.description,
              value: prediction,
            }))}
            value={values.address}
            onChange={(_evt, value) => {
              // `null` = cleared via the clear (X) button. setFieldValue
              // validates with the new value; setFieldTouched must NOT
              // re-validate (it would run against the stale value and wipe the
              // just-computed error — e.g. clearing the required address).
              if (value == null) {
                setFieldValue("address", "");
                setFieldTouched("address", true, false);
              } else if (typeof value === "object") {
                setFieldValue("address", String(value.label));
                setFieldTouched("address", true, false);
              } else if (typeof value === "string") {
                setFieldValue("address", value);
                setFieldTouched("address", true, false);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                data-testid="address"
                name="address"
                error={touched.address && !!errors.address}
                helperText={touched.address && errors.address}
                onChange={(evt) => {
                  getPlacePredictions({ input: evt.target.value?.trim() });
                  setFieldValue("address", evt.target.value);
                  setFieldTouched("address", true, false);
                }}
                onBlur={() => {
                  setFieldTouched("address", true);
                  validateField("address");
                }}
              />
            )}
          />
          <Typography sx={fieldLabelSx}>Service Duration *</Typography>
          <TextField
            id="serviceTime"
            type="number"
            value={serviceTime.value}
            onChange={serviceTime.onChange}
            onFocus={serviceTime.onFocus}
            onBlur={serviceTime.onBlur}
            error={serviceTime.showError}
            helperText={serviceTime.helperText}
            inputProps={serviceTime.inputProps}
            margin="dense"
            variant="outlined"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">minutes</InputAdornment>
              ),
            }}
            sx={numericFieldSx}
          />
          <Typography sx={fieldLabelSx}>Snow Reporter</Typography>
          <Checkbox
            id="reportsEnabled"
            checked={values.reportsEnabled}
            sx={{ justifySelf: "flex-start" }}
            onChange={handleChange}
          />

          <Box sx={{ gridColumn: "1 / -1" }}>
            <CustomerFinancesSection
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
            />
            {showMore && (
              <CrmDetailsFields
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                sectionOrder={["onsiteContact", "drivewayInfo"]}
              />
            )}
            <Button
              variant="text"
              onClick={() => setShowMore(!showMore)}
              endIcon={showMore ? <ArrowDropUp /> : <ArrowDropDown />}
              sx={{ mt: "8px" }}
              data-testid={showMore ? "show-less-button" : "show-more-button"}
            >
              {showMore ? "Show Less" : "Show More"}
            </Button>
          </Box>
        </Box>
      )}
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  container: {
    mt: "40px",
    display: "grid",
    // Match FieldRow (label 260 / gap 24 / input 360) so all sections align.
    gridTemplateColumns: "260px 360px",
    columnGap: "24px",
    rowGap: "16px",
    alignItems: "center",
  },
  sectionTitle: {
    gridColumn: "1 / -1",
    fontWeight: 400,
    fontSize: "22px",
    lineHeight: "28px",
    color: "#c5c6c9",
  },
}));

export default ConvertLeadPage;
